/**
 * Filters for field events.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { DatePicker } from '@/components/forms/DatePicker';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { useEstablishments } from '@/hooks/useEstablishments';
import { getLevels } from '@/services/levels.service';
import { getActivityTypes } from '@/services/activity-types.service';
import { getActivities } from '@/services/activities.service';
import {
  EVENT_STATUS_CONFIG,
  EVENT_RECURRENCE_LABELS,
  DATE_RANGE_PERIOD_OPTIONS_WITH_CUSTOM,
  type EventFilters,
  type FilterGroupId,
} from './event-configs';
import type { EventStatus, RecurrenceType } from '@/types/event.types';

/** Props for {@link EventFiltersContent}. */
interface EventFiltersContentProps {
  filters: EventFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onFiltersChange: (filters: EventFilters) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: FilterGroupId,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Filters for field events. */
export const EventFiltersContent: React.FC<EventFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onFiltersChange,
}) => {
  const { data: estabData } = useEstablishments();
  const establishments = estabData?.data || [];

  const { data: levelsData } = useQuery({
    queryKey: ['levels', 'all'],
    queryFn: async () => {
      const res = await getLevels();
      return res.data;
    },
  });
  const levels = levelsData || [];

  const { data: activityTypesData } = useQuery({
    queryKey: ['activity-types', 'all'],
    queryFn: () => getActivityTypes({ pageSize: 10 }),
  });
  const activityTypes = activityTypesData?.data || [];

  const { data: activitiesData } = useQuery({
    queryKey: ['activities', 'by-type', filters.activityTypeIds],
    queryFn: () =>
      getActivities({
        activityTypeIds:
          filters.activityTypeIds && filters.activityTypeIds.length > 0
            ? filters.activityTypeIds
            : undefined,
        pageSize: 10,
      }),
    enabled: !!(filters.activityTypeIds && filters.activityTypeIds.length > 0),
  });
  const activities = activitiesData?.data || [];

  /** Patch one filter key and notify the parent. */
  const handleFilterChange = <K extends keyof EventFilters>(key: K, value: EventFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  /** Toggle an event-status chip (multi-select; empty → undefined). */
  const handleToggleStatus = (status: EventStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter((s) => s !== status)
      : [...currentStatuses, status];
    handleFilterChange('status', newStatuses.length > 0 ? newStatuses : undefined);
  };

  /** Set the period chip; non-custom clears the date range. */
  const handleSetPeriod = (period: EventFilters['period']) => {
    onFiltersChange({
      ...filters,
      period,
      ...(period !== 'custom' && { dateFrom: undefined, dateTo: undefined }),
    });
  };

  const recurrenceOptions = Object.entries(EVENT_RECURRENCE_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const establishmentOptions = establishments.map((e: { id: string; name: string }) => ({
    value: e.id,
    label: e.name,
  }));

  const levelOptions = levels.map((l: { id: string; label: string }) => ({
    value: l.id,
    label: l.label,
  }));

  const activityTypeOptions = activityTypes.map((at: { id: string; label: string }) => ({
    value: at.id,
    label: at.label,
  }));

  const activityOptions = activities.map((a: { id: string; label: string }) => ({
    value: a.id,
    label: a.label,
  }));

  const show = (id: FilterGroupId) => isVisible(id, allowedFilterIds, visibleFilterIds);
  const showSearchOrPeriod =
    show('filter-search') ||
    show('filter-period') ||
    (show('filter-date-range') && filters.period === 'custom');

  return (
    <div className="space-y-4">
      {showSearchOrPeriod && (
        <div className="filter-groups-container">
          {show('filter-search') && (
            <div className="filter-group">
              <span className="filter-group-label">Recherche</span>
              <div className="w-56">
                <Input
                  placeholder="Rechercher un participant..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  inputSize="sm"
                />
              </div>
            </div>
          )}

          {show('filter-period') && (
            <div className="filter-group">
              <span className="filter-group-label">Période</span>
              <SegmentedControl
                size="sm"
                value={filters.period ?? 'month'}
                onChange={(v) => handleSetPeriod(v as EventFilters['period'])}
                options={[...DATE_RANGE_PERIOD_OPTIONS_WITH_CUSTOM]}
              />
            </div>
          )}

          {show('filter-date-range') && filters.period === 'custom' && (
            <>
              <div className="filter-group">
                <span className="filter-group-label">Date de début</span>
                <DatePicker
                  value={filters.dateFrom}
                  onChange={(value) => handleFilterChange('dateFrom', value)}
                  inputSize="sm"
                />
              </div>

              <div className="filter-group">
                <span className="filter-group-label">Date de fin</span>
                <DatePicker
                  value={filters.dateTo}
                  onChange={(value) => handleFilterChange('dateTo', value)}
                  inputSize="sm"
                />
              </div>
            </>
          )}
        </div>
      )}

      {show('filter-status') && (
        <div className="filter-groups-container">
          <div className="filter-group">
            <span className="filter-group-label">Statut</span>
            <div className="filter-options">
              {Object.entries(EVENT_STATUS_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const isActive = filters.status?.includes(key as EventStatus) ?? false;

                const getBadgeClassName = () => {
                  if (!isActive) return 'filter-badge-inactive';
                  if (config.filterActiveVariant === 'solid') return 'filter-badge-active-solid';
                  return 'filter-badge-active';
                };

                return (
                  <Button
                    key={key}
                    variant="custom"
                    size="sm"
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                      getBadgeClassName(),
                    )}
                    style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
                    onClick={() => handleToggleStatus(key as EventStatus)}
                  >
                    {config.isInProgress ? (
                      <span
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ background: isActive ? 'white' : 'rgb(var(--color-error))' }}
                      />
                    ) : (
                      <Icon className="w-3.5 h-3.5" />
                    )}
                    {config.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {(show('filter-establishment') ||
        show('filter-level') ||
        show('filter-recurrence') ||
        show('filter-activity-type') ||
        show('filter-activity')) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {show('filter-establishment') && (
            <MultiSelect
              label="Établissement"
              placeholder="Sélectionner des établissements"
              options={establishmentOptions}
              value={filters.establishmentIds || []}
              onChange={(value) => handleFilterChange('establishmentIds', value)}
              inputSize="sm"
            />
          )}

          {show('filter-level') && (
            <MultiSelect
              label="Niveau"
              placeholder="Sélectionner des niveaux"
              options={levelOptions}
              value={filters.levelIds || []}
              onChange={(value) => handleFilterChange('levelIds', value)}
              inputSize="sm"
            />
          )}

          {show('filter-recurrence') && (
            <Select
              label="Récurrence"
              placeholder="Sélectionner une récurrence"
              options={recurrenceOptions}
              value={filters.recurrenceType || ''}
              onChange={(value) => handleFilterChange('recurrenceType', value as RecurrenceType)}
              size="sm"
            />
          )}

          {show('filter-activity-type') && (
            <MultiSelect
              label="Type d'activité"
              placeholder="Sélectionner des types"
              options={activityTypeOptions}
              value={filters.activityTypeIds || []}
              onChange={(value) => {
                handleFilterChange('activityTypeIds', value);
                if (value.length === 0) {
                  handleFilterChange('activityIds', undefined);
                }
              }}
              inputSize="sm"
            />
          )}

          {show('filter-activity') &&
            filters.activityTypeIds &&
            filters.activityTypeIds.length > 0 && (
              <MultiSelect
                label="Activité"
                placeholder="Sélectionner des activités"
                options={activityOptions}
                value={filters.activityIds || []}
                onChange={(value) => handleFilterChange('activityIds', value)}
                inputSize="sm"
                disabled={activityOptions.length === 0}
              />
            )}
        </div>
      )}
    </div>
  );
};
